import { createModel } from '@rematch/core'
import * as firebase from 'firebase'
import * as R from 'ramda'
import { getContentType } from 'src/utils'
import { dispatch, select, store } from '../store'

interface IProgress {
  played: number
  playedSeconds: number
  loaded: number
  loadedSeconds: number
}

interface ILectureContentState {
  currentLectureId: string | null
  currentChapterIdx: number
  currentInsightId: string | null
  currentMaterialIdx: number
  currentReplyId: string | null
  showInsightEnded: boolean
  isPlaying: boolean
  playbackRateIdx: number
  progress: IProgress

  isSpeaker: boolean
  isParticipant: boolean
}

const initalState: ILectureContentState = {
  currentLectureId: null,
  currentChapterIdx: -1,
  currentInsightId: null,
  currentMaterialIdx: -1,
  currentReplyId: null,
  showInsightEnded: false,
  isPlaying: false,
  playbackRateIdx: 0,
  progress: {
    played: 0,
    playedSeconds: 0,
    loaded: 0,
    loadedSeconds: 0
  },

  isSpeaker: false,
  isParticipant: false
}

export default createModel({
  state: initalState,
  reducers: {
    updateLectureContentState: (
      prevState: ILectureContentState,
      updated: Partial<ILectureContentState>
    ): ILectureContentState => {
      return {
        ...prevState,
        ...updated
      }
    },
    openInsightEnded: (
      prevState: ILectureContentState
    ): ILectureContentState => ({
      ...prevState,
      showInsightEnded: true
    }),
    closeInsightEnded: (
      prevState: ILectureContentState
    ): ILectureContentState => ({
      ...prevState,
      showInsightEnded: false
    }),
    startPlaying: (prevState: ILectureContentState): ILectureContentState => ({
      ...prevState,
      isPlaying: true
    }),
    stopPlaying: (prevState: ILectureContentState): ILectureContentState => ({
      ...prevState,
      isPlaying: false
    }),
    updateProgress: (
      prevState: ILectureContentState,
      progress: IProgress
    ): ILectureContentState => ({
      ...prevState,
      progress
    }),
    switchPlaying: (prevState: ILectureContentState): ILectureContentState => {
      return {
        ...prevState,
        isPlaying: !prevState.isPlaying
      }
    },
    switchPlaybackRate: (
      prevState: ILectureContentState
    ): ILectureContentState => {
      return {
        ...prevState,
        playbackRateIdx: (prevState.playbackRateIdx + 1) % 4
      }
    }
  },
  effects: {
    async changeCurrentLecture(lectureId: string) {
      const rootState = store.getState()
      if (lectureId === rootState.lectureContent.currentLectureId) {
        return
      }
      const currentUser = firebase.auth().currentUser
      const currentLecture = select.lectures.getById(rootState, lectureId)
      const participants = select.participants.getByLectureId(
        rootState,
        lectureId
      )
      const isSpeaker =
        currentLecture !== null &&
        currentUser !== null &&
        R.contains(currentUser.uid, currentLecture.speakerIds)
      const isParticipant =
        currentUser !== null &&
        participants !== null &&
        R.contains(currentUser.uid, participants)
      dispatch.lectureContent.updateLectureContentState({
        currentLectureId: lectureId,
        currentChapterIdx: -1,
        currentInsightId: null,
        currentMaterialIdx: -1,
        currentReplyId: null,

        isSpeaker,
        isParticipant
      })
      dispatch.lectureDiscussion.fetchDiscussions()
    },
    async nextAudioMaterial() {
      const rootState = store.getState()
      const { lectures } = rootState
      const {
        currentLectureId,
        currentChapterIdx,
        currentInsightId,
        currentMaterialIdx
      } = rootState.lectureContent
      if (currentLectureId === null || currentInsightId === null) {
        return
      }
      // find next audio material
      const currentLecture = lectures[currentLectureId]
      const nextChapterIdx = currentChapterIdx
      let nextInsightId = currentInsightId
      let nextMaterialIdx = currentMaterialIdx
      while (true) {
        nextMaterialIdx += 1
        const insights = store.getState().insights
        const nextMaterial = nextInsightId
          ? insights[nextInsightId].materials[nextMaterialIdx]
          : null
        // next audio material found
        if (nextMaterial && getContentType(nextMaterial.metadata) === 'audio') {
          dispatch.lectureContent.updateLectureContentState({
            isPlaying: true,
            currentChapterIdx: nextChapterIdx,
            currentInsightId: nextInsightId,
            currentMaterialIdx: nextMaterialIdx
          })
          return
        }
        // move to next insight
        if (!nextMaterial) {
          const insightIds =
            currentLecture.units[0].chapters[nextChapterIdx].insightIds
          const insightIdx = R.indexOf(nextInsightId, insightIds)
          // move to next chapter
          if (insightIdx < 0 || insightIdx === insightIds.length - 1) {
            dispatch.lectureContent.openInsightEnded()
            dispatch.lectureContent.stopPlaying()
            return
          } else {
            nextInsightId = insightIds[insightIdx + 1]
            nextMaterialIdx = -1
          }
        }
      }
    }
  },
  selectors: {
    getById: (state: ILectureContentState, lectureContentId: string) => {
      return state[lectureContentId] ? state[lectureContentId] : null
    }
  }
})



// WEBPACK FOOTER //
// ./src/models/lectureContent.ts